import { App, Form } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    getApiError,
    getRouteMyRoutesThunk,
    getUserTeamGetByManagerThunk,
    patchSchedulerSetSchedulesByPoints,
} from "tm-shared/api";
import { Schedule } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import { ROLE_CHILDREN_MAP, ROUTES } from "@/constants";
import { useAsyncAction } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { ScheduleRepeatFormFields } from "@/pages/SV/RouteManagement/components/ScheduleRepeatForm/ScheduleRepeatForm.types";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import { BlockList } from "@/shared/pageComposition/BlockList";
import { Meta } from "@/shared/seo";
import { getSchedulerPointScheduleDetailsManagerThunk } from "@/store/slices/manager";
import { setCurrentScheduleList } from "@/store/slices/manager/manager.slice";

import { AddPointModel, getAddPointBlocks } from "./AddPoint.blocks";
import { getAddPointCapabilities } from "./AddPoint.policy";

function AddPoint() {
    const { routeId } = useParams();
    const { notification } = App.useApp();

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();

    const navigateOptions = useMemo(
        () => ({ state: location.state, replace: true }),
        [location.state],
    );

    const [scheduleRepeatForm] = Form.useForm<ScheduleRepeatFormFields>();

    const role = useProtectedUserRoleSelector();
    const capabilities = useMemo(() => getAddPointCapabilities(role), [role]);

    const userId = useAppSelector((s) => s.user.id);
    const currentScheduleList = useAppSelector(
        (state) => state.manager.currentScheduleList,
    );

    const [bootLoading, setBootLoading] = useState(false);
    const [checkedPoints, setCheckedPoints] = useState<Set<string>>(new Set());

    const routeEditorPath = routeId
        ? capabilities.isManager
            ? buildRoutePath(ROUTES.RouteManagementEdit, { routeId })
            : ROUTES.RouteManagementMyRoute
        : ROUTES.RouteManagementCreate;

    const handleCreatePoint = useCallback(() => {
        navigate(ROUTES.NewRoutePointCreate);
    }, [navigate]);

    const togglePointChecked = useCallback((pointId: string) => {
        setCheckedPoints((prev) => {
            const next = new Set(prev);

            if (next.has(pointId)) next.delete(pointId);
            else next.add(pointId);

            return next;
        });
    }, []);

    useEffect(() => {
        if (!capabilities.canFetchTeamRoutes) return;

        let alive = true;

        (async () => {
            setBootLoading(true);

            await Promise.allSettled([
                dispatch(
                    getRouteMyRoutesThunk({
                        ownerId: userId,
                        roleSlug: ROLE_CHILDREN_MAP[role]!,
                    }),
                ).unwrap(),
                dispatch(
                    getUserTeamGetByManagerThunk({ managerId: userId }),
                ).unwrap(),
            ]);

            if (alive) setBootLoading(false);
        })();

        return () => {
            alive = false;
        };
    }, [userId, dispatch, role, capabilities.canFetchTeamRoutes]);

    const [handleUpdatePoint, updateLoading] = useAsyncAction(async () => {
        if (!checkedPoints.size) return;

        let values: ScheduleRepeatFormFields;
        try {
            values = await scheduleRepeatForm.validateFields();
        } catch {
            return;
        }

        const { weekStartDate, weekly, days } = values;

        const newSchedules: Schedule[] = Array.from(checkedPoints).map(
            (pointId) => ({
                pointId,
                marketId: pointId,
                weekly,
                days,
                startDate: weekStartDate,
                sortOrder: 1,
                updatedAt: dayjs().toISOString(),
            }),
        );

        const updatedRouteItems = [...currentScheduleList, ...newSchedules];

        if (!routeId) {
            dispatch(setCurrentScheduleList(updatedRouteItems));
            navigate(routeEditorPath, navigateOptions);
            return;
        }

        try {
            await patchSchedulerSetSchedulesByPoints({
                routeId,
                schedules: updatedRouteItems,
            });

            await dispatch(
                getSchedulerPointScheduleDetailsManagerThunk({
                    routeId,
                }),
            );

            navigate(routeEditorPath, navigateOptions);
        } catch (err) {
            console.error(err);
            const { displayMessage } = getApiError(err);

            notification.error({
                title: "Не вдалося додати ТТ на маршрут",
                description: displayMessage,
            });
        }
    });

    const model: AddPointModel = useMemo(
        () => ({
            capabilities,
            bootLoading,
            updateLoading,
            scheduleRepeatForm,
            checkedPoints,
            handlers: {
                togglePointChecked,
                handleUpdatePoint,
                handleCreatePoint,
            },
        }),
        [
            capabilities,
            bootLoading,
            updateLoading,
            scheduleRepeatForm,
            checkedPoints,
            togglePointChecked,
            handleUpdatePoint,
            handleCreatePoint,
        ],
    );

    const blocks = useMemo(() => getAddPointBlocks(model), [model]);

    return (
        <>
            <Meta title="Add point" />

            <Header
                title="Додати ТТ"
                path={routeEditorPath}
                locationState={navigateOptions}
            />

            <MainContainer $padding="0">
                <BlockList blocks={blocks} model={model} />
            </MainContainer>
        </>
    );
}

export default AddPoint;
